import dayjs from 'dayjs';
import { buildCheckpoints, checkpointSite } from './orderTracking.checkpoints';
import { drivenPath, trailWithin } from './orderTracking.driven';
import {
	distanceKm,
	withLiveTrip,
	withoutLiveEta,
} from './orderTracking.utils';
import { PublicOrderDto } from '../../../services/tracking.service';
import {
	TruckDto,
	TruckRouteDto,
	TruckTelematicsDto,
} from '../../../services/truck.services';
import { NotificationInterface } from '../../../components/OrdersTable/interfaces/Order.interface';
import {
	TrackingDocument,
	TrackingEta,
	TrackingLatLng,
	TrackingLocation,
	TrackingLocationStatus,
	TrackingOrder,
	TrackingRouteMode,
	TrackingStep,
	TrackingStepKey,
} from './orderTracking.interface';
import {
	TRACKING_LIVE_FIX_MAX_AGE_MS,
	TRACKING_STEPS,
	buyerStepsFrom,
} from './orderTracking.constants';
import {
	CUSTOMER_STEPS_MULTIPLE,
	CUSTOMER_STEPS_MULTIPLE_EXCLUDED,
	CUSTOMER_STEPS_SIMPLE,
	CUSTOMER_STEPS_SIMPLE_EXCLUDED,
} from '../../../components/OrderDetailsStepper/stepData';
import {
	displayedTruckStatus,
	resolveExecutionStatus,
} from '../../../utils/order-execution.util';

const STEP_RANK: Record<string, number> = {
	requested: 0,
	to_do: 0,
	created: 0,
	accepted: 1,
	sent_to_driver: 1,
	awaiting_confirmation: 1,
	transporter_confirmed: 1,
	truck_confirmed: 1,
	in_progress: 1,
	to_loading: 2,
	arrived_to_loading: 3,
	loading: 3,
	confirm_pick_up: 3,
	loaded: 4,
	to_unloading: 5,
	arrived_to_unloading: 5,
	unloading: 5,
	unloaded: 6,
	delivered: 7,
	pending_payment: 7,
	completed: 7,
	paid: 7,
};

const STEP_NOTIFICATION: Partial<Record<TrackingStepKey, string[]>> = {
	accepted: ['order_accepted_by_driver'],
	to_loading: ['order_start_trip_to_loading'],
	arrived_to_loading: ['order_arrived_to_loading', 'order_start_loading_goods'],
	loaded: ['order_confirm_loading_goods', 'order_producer_loading_goods'],
	to_unloading: [
		'order_start_trip_to_unloading',
		'order_arrived_to_unloading',
		'order_start_unloading_goods',
	],
	unloaded: ['order_confirm_unloading_goods'],
	completed: ['order_complete', 'order_paid'],
};

const DONE_STATUSES = ['unloaded', 'completed', 'pending_payment', 'paid'];

const FINISHED_ORDER_STATUSES = ['completed', 'pending_payment', 'paid'];

const FINAL_RANK = TRACKING_STEPS.length;

const truckRank = (truck: TruckDto, orderFinished: boolean): number =>
	orderFinished ? FINAL_RANK : rankOf(displayedTruckStatus(truck.orderStatus));

const isOnTrip = (rank: number): boolean => rank >= 2 && rank < 6;

export const toLatLng = (point?: {
	coordinates?: number[];
}): TrackingLatLng | undefined =>
	point?.coordinates?.length === 2
		? { lat: point.coordinates[1], lng: point.coordinates[0] }
		: undefined;

const rankOf = (status?: string): number => STEP_RANK[status ?? ''] ?? 0;

const productName = (order: PublicOrderDto): string => {
	const language = localStorage.getItem('lang') || 'en';
	return (
		order.product?.translations?.find((item) => item.language === language)
			?.name ||
		order.contract?.lines?.[0]?.product?.translations?.find(
			(item) => item.language === language,
		)?.name ||
		order.product?.name ||
		''
	);
};

const hasProducerLoadDetails = (truck: TruckDto): boolean =>
	'producerLoadDetails' in truck;

const buyerProgress = (
	order: PublicOrderDto,
	trucks: TruckDto[],
): { steps: string[]; current: number } => {
	const status = String(order.status);
	const state = String(order.state);
	const simple = trucks.length <= 1;
	const included = order.deliveryIncluded !== false;

	if (simple && included) {
		const steps = CUSTOMER_STEPS_SIMPLE;
		const execution =
			resolveExecutionStatus({ state, deliveryIncluded: included, trucks }) ??
			'';
		if (status === 'paid') return { steps, current: 6 };
		if (
			['to_do', 'awaiting_confirmation', 'transporter_confirmed'].includes(
				execution,
			)
		)
			return { steps, current: 1 };
		if (
			[
				'truck_confirmed',
				'requested',
				'accepted',
				'to_loading',
				'arrived_to_loading',
				'loading',
			].includes(execution)
		)
			return { steps, current: 2 };
		if (['loaded', 'to_unloading'].includes(execution))
			return { steps, current: 3 };
		if (['arrived_to_unloading', 'unloading', 'unloaded'].includes(execution))
			return { steps, current: 4 };
		if (execution === 'completed') return { steps, current: 5 };
		return { steps, current: 1 };
	}

	if (!simple && included) {
		const steps = CUSTOMER_STEPS_MULTIPLE;
		if (status === 'paid') return { steps, current: 5 };
		if (trucks.every((truck) => truck.orderStatus === 'completed'))
			return { steps, current: 4 };
		if (
			[
				'preparing',
				'to_unloading',
				'arrived_to_unloading',
				'delivered',
			].includes(status)
		)
			return { steps, current: 3 };
		if (state === 'to_do') return { steps, current: 1 };
		if (
			[
				'awaiting_confirmation',
				'transporter_confirmed',
				'truck_confirmed',
			].includes(state)
		)
			return { steps, current: 2 };
		return { steps, current: 1 };
	}

	if (simple) {
		const steps = CUSTOMER_STEPS_SIMPLE_EXCLUDED;
		const current =
			{ created: 1, preparing: 2, in_progress: 3, delivered: 4, paid: 5 }[
				status
			] ?? 1;
		return { steps, current };
	}

	const steps = CUSTOMER_STEPS_MULTIPLE_EXCLUDED;
	if (
		['in_progress', 'preparing'].includes(status) &&
		trucks.some(hasProducerLoadDetails)
	)
		return { steps, current: 3 };
	if (status === 'delivered' && trucks.every(hasProducerLoadDetails))
		return { steps, current: 4 };
	const current =
		{ created: 1, preparing: 2, in_progress: 2, delivered: 3, paid: 5 }[
			status
		] ?? 1;
	return { steps, current };
};

interface UploadMeta {
	name?: string;
	uploadedAt?: string;
	uploadedByRole?: string;
}

interface LoadDetailsLike {
	documents?: string[];
	uploads?: UploadMeta[];
}

const isAbsoluteUrl = (value: string): boolean => /^https?:\/\//i.test(value);

const documentsFrom = (details?: LoadDetailsLike | null): TrackingDocument[] =>
	(details?.documents ?? []).map((entry) => {
		const fileName = decodeURIComponent(
			entry.split('?')[0].split('/').pop() || '',
		);
		const upload = details?.uploads?.find((item) => item.name === fileName);
		return {
			name: fileName || entry,
			url: isAbsoluteUrl(entry) ? entry : undefined,
			extension: (fileName.split('.').pop() || 'file').toUpperCase(),
			uploadedAt: upload?.uploadedAt,
			uploadedByRole: upload?.uploadedByRole,
		};
	});

const finiteNumber = (value: unknown): number | undefined => {
	const number = typeof value === 'number' ? value : parseFloat(String(value));
	return Number.isFinite(number) ? number : undefined;
};

/**
 * The fix the driver app sent with an action: `{ lat, long }` as the API
 * defines it, read from a notification's content (at its root or under
 * `location`) or from the load / unload details.
 */
export const readActionLocation = (
	value: unknown,
): { position: TrackingLatLng; at?: string } | undefined => {
	const raw = (value ?? {}) as Record<string, unknown>;
	const source = (raw.location ?? raw) as Record<string, unknown>;
	const lat = finiteNumber(source?.lat);
	const lng = finiteNumber(source?.lng ?? source?.long);
	if (lat === undefined || lng === undefined) return undefined;
	const pressed = finiteNumber(source?.recordedAt);
	return {
		position: { lat, lng },
		at: pressed && pressed > 0 ? new Date(pressed).toISOString() : undefined,
	};
};

const actionOf = (
	truck: TruckDto,
	key: TrackingStepKey,
	notifications: NotificationInterface[],
	singleTruck: boolean,
): NotificationInterface | undefined => {
	const categories = STEP_NOTIFICATION[key] ?? [];
	const belongsToTruck = (item: NotificationInterface) => {
		const content = item.content ?? {};
		if (content.driverId && truck.driver?.uid) {
			return content.driverId === truck.driver.uid;
		}
		if (content.extensionId !== undefined) {
			return Number(content.extensionId) === truck.id;
		}
		return singleTruck;
	};
	const matches = notifications
		.filter(
			(item) => categories.includes(item.category) && belongsToTruck(item),
		)
		.sort(
			(a, b) =>
				new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
		);
	return matches[0];
};

/** Where and when the driver did the step, from the app's own fix. */
const actionLocationOf = (
	truck: TruckDto,
	key: TrackingStepKey,
	notifications: NotificationInterface[],
	singleTruck: boolean,
): { position: TrackingLatLng; at?: string } | undefined => {
	const notified = readActionLocation(
		actionOf(truck, key, notifications, singleTruck)?.content,
	);
	if (notified) return notified;
	if (key === 'loaded') {
		return (
			readActionLocation(truck.loadDetails?.location) ??
			readActionLocation(truck.producerLoadDetails?.location)
		);
	}
	if (key === 'unloaded')
		return readActionLocation(truck.unloadDetails?.location);
	return undefined;
};

const withCheckpoints = (
	steps: TrackingStep[],
	telematics: TruckTelematicsDto | undefined,
	pickup: TrackingLatLng,
	delivery: TrackingLatLng,
	addresses: { pickup: string; delivery: string },
) => ({
	steps,
	// A pin standing at the site already knows its address.
	checkpoints: buildCheckpoints(steps, telematics, pickup, delivery).map(
		(checkpoint) => {
			if (checkpoint.source !== 'site') return checkpoint;
			const site = checkpointSite(checkpoint.key);
			const place = site ? addresses[site] : undefined;
			return place ? { ...checkpoint, place } : checkpoint;
		},
	),
});

const buildSteps = (
	truck: TruckDto,
	notifications: NotificationInterface[],
	singleTruck: boolean,
	rank: number,
): TrackingStep[] => {
	return TRACKING_STEPS.map((step, index) => {
		const completed = index < rank;
		const documents =
			step.key === 'loaded'
				? documentsFrom(truck.loadDetails).concat(
						documentsFrom(truck.producerLoadDetails),
				  )
				: step.key === 'unloaded'
				? documentsFrom(truck.unloadDetails)
				: [];

		const action = completed
			? actionLocationOf(truck, step.key, notifications, singleTruck)
			: undefined;
		return {
			key: step.key,
			completed,
			// the moment the driver pressed the button beats the notification time
			completedAt: completed
				? action?.at ??
				  actionOf(truck, step.key, notifications, singleTruck)?.createdAt ??
				  documents.find((doc) => doc.uploadedAt)?.uploadedAt
				: undefined,
			location: action?.position,
			documents: documents.length ? documents : undefined,
		};
	});
};

/** The span of a truck's GPS history that is this order's (epoch ms). */
export interface TripWindow {
	/** the driver pressed "en route to loading"; Infinity until that is known */
	from?: number;
	/** the goods were unloaded; open while the trip goes on */
	to?: number;
}

const epochOf = (value?: string): number | undefined => {
	const time = value ? Date.parse(value) : NaN;
	return Number.isFinite(time) ? time : undefined;
};

/**
 * A truck serves other orders before and after this one (the dashboard
 * assigns it to the next order while it still drives this one), so of its
 * history only this order's trip counts. It starts the moment the driver
 * pressed "en route to loading" in the app for this order (the
 * `order_start_trip_to_loading` notification); without that moment there is
 * no history. It ends at the unloading, or at the truck's last change when
 * that time is missing.
 */
const tripWindow = (
	truck: TruckDto,
	steps: TrackingStep[],
	rank: number,
): TripWindow => {
	const timeOf = (key: TrackingStepKey): number | undefined =>
		epochOf(steps.find((step) => step.key === key)?.completedAt);
	const from = timeOf('to_loading');
	if (rank < 2 || from === undefined) return { from: Infinity };
	if (rank < 6) return { from };
	const to =
		timeOf('unloaded') ?? timeOf('completed') ?? epochOf(truck.updatedAt);
	return { from, to };
};

export const tripWindowOf = (
	order: PublicOrderDto,
	truck: TruckDto,
): TripWindow => {
	const singleTruck = (order.trucks ?? []).length <= 1;
	const orderFinished = FINISHED_ORDER_STATUSES.includes(String(order.status));
	const rank = truckRank(truck, orderFinished);
	const notifications = order.notifications?.items ?? [];
	return tripWindow(
		truck,
		buildSteps(truck, notifications, singleTruck, rank),
		rank,
	);
};

const hasTimedTrail = (telematics?: TruckTelematicsDto): boolean =>
	!!telematics?.trailAt &&
	telematics.trailAt.length === (telematics.trail?.length ?? 0);

const parseTime = (value?: string, day?: string): dayjs.Dayjs | undefined => {
	if (!value) return undefined;
	if (/^\d{2}:\d{2}$/.test(value)) {
		const base = day ? dayjs(day) : dayjs();
		if (!base.isValid()) return undefined;
		return dayjs(`${base.format('YYYY-MM-DD')}T${value}`);
	}
	const parsed = dayjs(value);
	return parsed.isValid() ? parsed : undefined;
};

const etaOf = (truck: TruckDto): TrackingEta | undefined => {
	const day = truck.deliveryTo || truck.deliveryFrom;
	const from = parseTime(truck.deliveryTimeFrom, day);
	if (!from) return undefined;
	return {
		from: from.toISOString(),
		to: parseTime(truck.deliveryTimeTo, day)?.toISOString(),
	};
};

const isFiniteLatLng = (point: unknown): point is [number, number] =>
	Array.isArray(point) &&
	Number.isFinite(point[0]) &&
	Number.isFinite(point[1]);

const parseTrail = (
	telematics?: TruckTelematicsDto,
): TrackingLatLng[] | undefined => {
	const trail = (telematics?.trail ?? [])
		.filter(isFiniteLatLng)
		.map(([lat, lng]) => ({ lat, lng }));
	return trail.length > 1 ? trail : undefined;
};

const telematicsPosition = (
	telematics?: TruckTelematicsDto,
): TrackingLatLng | undefined =>
	telematics &&
	Number.isFinite(telematics.position?.lat) &&
	Number.isFinite(telematics.position?.lng)
		? { lat: telematics.position.lat, lng: telematics.position.lng }
		: undefined;

const isFreshFix = (recordedAt?: string): boolean => {
	if (!recordedAt) return false;
	const time = dayjs(recordedAt);
	return (
		time.isValid() &&
		dayjs().diff(time, 'millisecond') <= TRACKING_LIVE_FIX_MAX_AGE_MS
	);
};

/** A JSON `[[lat, lng], ...]` string as pairs; nothing for a bad shape. */
const parseShape = (shape?: string): [number, number][] | undefined => {
	if (!shape) return undefined;
	try {
		const points = JSON.parse(shape) as unknown;
		if (!Array.isArray(points)) return undefined;
		const pairs = points
			.filter(isFiniteLatLng)
			.map(([lat, lng]) => [lat, lng] as [number, number]);
		return pairs.length > 1 ? pairs : undefined;
	} catch (e) {
		return undefined;
	}
};

const parseRoute = (route?: TruckRouteDto): TrackingLatLng[] | undefined =>
	parseShape(route?.shape)?.map(([lat, lng]) => ({ lat, lng }));

const projectOnRoute = (
	position: TrackingLatLng,
	route: TrackingLatLng[],
): number => {
	let nearest = 0;
	let nearestDistance = Infinity;
	route.forEach((point, index) => {
		const distance = distanceKm(point, position);
		if (distance < nearestDistance) {
			nearestDistance = distance;
			nearest = index;
		}
	});
	let covered = 0;
	let total = 0;
	for (let i = 1; i < route.length; i++) {
		const segment = distanceKm(route[i - 1], route[i]);
		total += segment;
		if (i <= nearest) covered += segment;
	}
	return total ? covered / total : 0;
};

const progressOf = (
	rank: number,
	position: TrackingLatLng | undefined,
	pickup: TrackingLatLng,
	delivery: TrackingLatLng,
	route?: TrackingLatLng[],
): number => {
	if (rank <= 4) return 0;
	if (rank >= 6) return 1;
	if (!position) return 0.5;
	if (route) return Math.min(1, Math.max(0, projectOnRoute(position, route)));
	const total = distanceKm(pickup, delivery);
	if (!total) return 0.5;
	return Math.min(1, Math.max(0, distanceKm(pickup, position) / total));
};

const locationStatus = (
	trucks: TruckDto[],
	doneKey: TrackingStepKey,
	activeStatuses: string[],
): TrackingLocationStatus => {
	const doneRank = TRACKING_STEPS.findIndex((step) => step.key === doneKey) + 1;
	const statuses = trucks.map((truck) =>
		displayedTruckStatus(truck.orderStatus),
	);
	if (trucks.length && statuses.every((status) => rankOf(status) >= doneRank)) {
		return 'completed';
	}
	if (statuses.some((status) => activeStatuses.includes(status ?? ''))) {
		return 'in_progress';
	}
	return 'scheduled';
};

const windowOf = (
	trucks: TruckDto[],
	field: 'deliveryFrom' | 'deliveryTo',
): Pick<TrackingLocation, 'date' | 'timeFrom' | 'timeTo'> => {
	const dated = trucks
		.map((truck) => ({ truck, day: dayjs(truck[field]) }))
		.filter((item) => item.day.isValid())
		.sort((a, b) => a.day.valueOf() - b.day.valueOf());
	const first = dated[0];
	if (!first) return {};
	return {
		date: first.day.toISOString(),
		timeFrom: parseTime(
			first.truck.deliveryTimeFrom,
			first.truck[field],
		)?.format('HH:mm'),
		timeTo: parseTime(first.truck.deliveryTimeTo, first.truck[field])?.format(
			'HH:mm',
		),
	};
};

export interface MapPublicOrderOptions {
	showPendingWithFreshPosition?: boolean;
	fallbackRoute?: TruckRouteDto;
	routeMode?: TrackingRouteMode;
}

const LEFT_PICKUP_RANK = 5;

const VIEWER_CHECKPOINTS: Record<TrackingRouteMode, TrackingStepKey[] | null> =
	{
		full: null,
		toPickup: ['accepted', 'to_loading', 'arrived_to_loading', 'loaded'],
		toDelivery: ['loaded', 'to_unloading', 'unloaded', 'completed'],
	};

const visibleToViewer = (mode: TrackingRouteMode, rank: number): boolean =>
	mode === 'full' ||
	(mode === 'toDelivery' ? rank >= LEFT_PICKUP_RANK : rank < LEFT_PICKUP_RANK);

export const mapPublicOrder = (
	order: PublicOrderDto,
	options: MapPublicOrderOptions = {},
): TrackingOrder => {
	const trucks = (order.trucks ?? []).slice().sort((a, b) => a.id - b.id);
	const notifications = order.notifications?.items ?? [];
	const singleTruck = trucks.length <= 1;
	const progress = buyerProgress(order, trucks);
	const orderFinished = FINISHED_ORDER_STATUSES.includes(String(order.status));
	const truckRoutes = new Map(
		trucks.map((truck) => [truck.id, parseRoute(truck.route)] as const),
	);
	const orderRoute =
		Array.from(truckRoutes.values()).find(Boolean) ??
		parseRoute(options.fallbackRoute);
	const pickup = toLatLng(order.pickupLocation) ?? { lat: 0, lng: 0 };
	const delivery = toLatLng(order.deliveryLocation) ?? pickup;
	const orderedQuantity = Number(order.quantity ?? 0);
	const orderDistanceKm = Number(order.distance ?? trucks[0]?.distance ?? 0);
	const loadedQuantity =
		order.loadedQuantity ??
		trucks.reduce((sum, truck) => sum + Number(truck.loadedQuantity ?? 0), 0);

	return {
		id: order.id,
		orderNumber: String(order.orderId ?? order.id),
		placedAt: order.createdAt,
		updatedAt: order.updatedAt,
		deliveryMode: trucks.length > 1 ? 'multiple' : 'single',
		transportIncluded: order.deliveryIncluded !== false,
		status: String(order.status),
		buyerSteps: buyerStepsFrom(progress.steps),
		buyerCurrentStep: progress.current,
		productName: productName(order),
		productType: order.product?.type,
		productSize: order.product?.size,
		unit: order.product?.unit === 'm3' ? 'm³' : 't',
		orderedQuantity,
		loadedQuantity,
		deliveriesDone: orderFinished
			? trucks.length
			: trucks.filter((truck) => DONE_STATUSES.includes(truck.orderStatus))
					.length,
		deliveriesTotal: trucks.length,
		contractNumber: order.contractId ? String(order.contractId) : undefined,
		distanceKm: orderDistanceKm,
		pickup: {
			address: order.pickupAddress ?? '',
			countryCode: order.pickupCountryCode,
			position: pickup,
			status: locationStatus(trucks, 'loaded', [
				'arrived_to_loading',
				'loading',
				'confirm_pick_up',
			]),
			...windowOf(trucks, 'deliveryFrom'),
		},
		delivery: {
			address: order.deliveryAddress ?? '',
			countryCode: order.deliveryCountryCode,
			position: delivery,
			status: locationStatus(trucks, 'unloaded', [
				'arrived_to_unloading',
				'unloading',
			]),
			...windowOf(trucks, 'deliveryTo'),
		},
		route: orderRoute ?? [pickup, delivery],
		routeKnown: !!orderRoute,
		trucks: trucks.map((truck, index) => {
			const telematics = truck.telematics;
			const rank = truckRank(truck, orderFinished);
			const onTrip = isOnTrip(rank);
			// Only a recorded fix places the truck; the driver's stored location
			// (usually the fleet base) never does.
			const pendingWithFreshFix =
				!!options.showPendingWithFreshPosition &&
				rank < 2 &&
				(isFreshFix(telematics?.recordedAt) ||
					isFreshFix(telematics?.lastSeenAt));
			const gpsPosition = telematicsPosition(telematics);
			const position = onTrip || pendingWithFreshFix ? gpsPosition : undefined;
			const route = truckRoutes.get(truck.id);
			const fresh = isFreshFix(telematics?.recordedAt);
			const idleSince = telematics?.idleSince ?? undefined;

			const liveSpeedKmh =
				onTrip && fresh && !idleSince
					? telematics?.avgSpeedKmh ?? telematics?.speedKmh
					: undefined;
			const routeDistanceKm =
				route && truck.route?.distance
					? truck.route.distance / 1000
					: undefined;
			const routeProgress = progressOf(rank, position, pickup, delivery, route);
			const stepsWithCheckpoints = withCheckpoints(
				buildSteps(truck, notifications, singleTruck, rank),
				telematics,
				pickup,
				delivery,
				{
					pickup: order.pickupAddress ?? '',
					delivery: order.deliveryAddress ?? '',
				},
			);
			const stepTime = (key: TrackingStepKey) => {
				const at = stepsWithCheckpoints.steps.find(
					(step) => step.key === key,
				)?.completedAt;
				const time = at ? Date.parse(at) : NaN;
				return Number.isFinite(time) ? time : undefined;
			};
			const routeMode = options.routeMode ?? 'full';
			const loadedAt = stepTime('loaded');
			const visible = visibleToViewer(routeMode, rank);
			// The part of the GPS trail this viewer may see.
			// Without a recorded trail, the backend's driven shape (already cut
			// to the loading → unloading leg) is the history.
			const drivenShape = parseShape(truck.route?.drivenShape);
			const trailScoped = !telematics?.trail?.length && !!drivenShape;
			const historyTelematics: TruckTelematicsDto | undefined = trailScoped
				? {
						...(telematics ?? {
							position: { lat: NaN, lng: NaN },
							recordedAt: '',
							idleSince: null,
						}),
						trail: drivenShape!,
						trailAt: undefined,
				  }
				: telematics;
			// The same truck drives other orders too: only the trail recorded
			// during this order's trip is its history. A trail without times
			// cannot be cut and is taken as it comes.
			const trip = tripWindow(truck, stepsWithCheckpoints.steps, rank);
			const orderTelematics = hasTimedTrail(historyTelematics)
				? trailWithin(historyTelematics, trip.from, trip.to)
				: historyTelematics;
			const viewerTelematics =
				routeMode === 'full' || trailScoped
					? orderTelematics
					: routeMode === 'toDelivery'
					? trailWithin(orderTelematics, loadedAt ?? Infinity)
					: loadedAt !== undefined
					? trailWithin(orderTelematics, undefined, loadedAt)
					: rank < LEFT_PICKUP_RANK
					? orderTelematics
					: trailWithin(orderTelematics, Infinity);
			const checkpointKeys = VIEWER_CHECKPOINTS[routeMode];
			const viewerCheckpoints = checkpointKeys
				? stepsWithCheckpoints.checkpoints.filter((checkpoint) =>
						checkpointKeys.includes(checkpoint.key),
				  )
				: stepsWithCheckpoints.checkpoints;
			const mapped = {
				id: truck.id,
				number: index + 1,
				plate: truck.driver?.licensePlates ?? '',
				driverName: truck.driver?.driverFullName ?? truck.driver?.name ?? '',
				speedKmh:
					onTrip && fresh && !idleSince
						? Math.round(telematics?.speedKmh ?? 0)
						: 0,
				stoppedSince: onTrip && fresh ? idleSince : undefined,
				lastSeenAt: telematics?.lastSeenAt ?? telematics?.recordedAt,
				positionAt: telematics?.recordedAt,
				position,
				positionSource: position
					? gpsPosition
						? ('gps' as const)
						: ('stored' as const)
					: undefined,
				signalLost:
					onTrip &&
					!!position &&
					(telematics?.isOnline === false ||
						(!fresh && !isFreshFix(telematics?.lastSeenAt))),
				trail: parseTrail(viewerTelematics),
				route,
				routeDistanceKm,
				routeProgress,
				eta: etaOf(truck),
				liveSpeedKmh,
				driven: drivenPath(viewerTelematics),
				steps: stepsWithCheckpoints.steps,
				checkpoints: viewerCheckpoints,
			};
			return visible
				? withLiveTrip(
						mapped,
						{
							route: orderRoute ?? [pickup, delivery],
							routeKnown: !!orderRoute,
						},
						routeMode,
				  )
				: {
						...withoutLiveEta(mapped),
						onMap: false,
						position: undefined,
						speedKmh: 0,
						stoppedSince: undefined,
						lastSeenAt: undefined,
						positionAt: undefined,
						positionSource: undefined,
						signalLost: false,
						liveSpeedKmh: undefined,
				  };
		}),
	};
};
